import { useEffect, useMemo, useState } from "react";
import { hexToRgba } from "../../utilities";
import Button from "../BundlesComponents/Button";
import {
    AvailableBXGYTemplates,
    AvailableFBTTemplates,
    AvailableStandardTemplates,
    AvailableVolumeTemplates,
    buildMetafieldFromSwatchOptions,
    BundleTypes,
    DISCOUNT_TYPES,
    getDiscountValueWithSign,
    getFinalPrice,
    getPriceLabel,
    renderBundleErrorMessage,
} from "../constants";
import { BxgyConfigInterface, ProductObj, StandardConfigInterface, VariantsObj } from "../Types";
import { CustomSwatches, PoweredByKefiBranding, Swatches, VariantSelectorV2 } from "./BundlePreviewWrapper";
import styles from "./BundlePreviewWrapper.module.css";
import Tooltip from "../../../components/Tooltip/Tooltip";
import { isDesktopUIForMixMatch, addBundleToCart } from "../RenderOnStore/utilities";
import { InspectorEditableBlock } from "../CommonFields/InspectorEditableBlock";
import { MAYUR_TEST_STORE, SPORTS_TOP_URL } from "../../constants";

interface BundlePreviewFooter {
    isFBT: boolean;
    standAlone: boolean;
    totalPrice: number;
    finalPrice: number;
    discountedPrice?: number;
    compareAtPriceTotal?: number;
    money_format: string;
    currency: string;
    bundleConfig: any;
    loading: boolean;
    otherLoading?: boolean;
    unavailableDiscountSelected: { [key: number]: number[] };
    addToCart: () => void;
    productPageVisible: (bundleConfig: any) => boolean;
    targetForm: any;
    isMainProductOutOfStock?: boolean;
    isMobile: boolean;
    bundleWrapperWidth?: number;
    quantity?: number;
    setQuantity?: React.Dispatch<React.SetStateAction<number>>;
}

export const FreeGiftVariantSelector = ({
    variants = [],
    selected,
    handleChange = () => {},
    productCardIndex,
    realProduct,
    bundlesDesign,
    bundleType,
}: {
    variants: VariantsObj[];
    selected: VariantsObj[];
    bundlesDesign: any;
    handleChange: any;
    productCardIndex: number;
    formId: string | undefined;
    bundleType: string;
    standAlone: boolean;
    realProduct: ProductObj;
}) => {
    const handleOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOptionsArray =
            e.target.value
                ?.split(" / ")
                ?.map((val) => val.trim()) // Trim spaces around each value
                ?.filter((val) => val !== "") || [];

        const matchingVariant = findSelectedVariant(selectedOptionsArray);
        if (matchingVariant) {
            const curVar = selected[productCardIndex];
            const curSub = realProduct?.subscriptions?.find((ss) => ss?.id == curVar?.selling_plan);
            const updatedMatchingVariant = {
                ...matchingVariant,
                selling_plan: curVar?.selling_plan || "",
                copyPrice: matchingVariant.price,
                price: curSub
                    ? getFinalPrice(curSub?.discountType, curSub.discountValue, +matchingVariant.copyPrice).toString()
                    : matchingVariant.price,
            };

            const updatedSelectedVariants = [...selected];
            updatedSelectedVariants[productCardIndex] = updatedMatchingVariant; // <-- Update with productCardIndex
            handleChange(updatedSelectedVariants);
        } else {
            console._log("Matching variant not found!");
        }
    };

    const findSelectedVariant = (selectedOptionsArray: string[]) => {
        for (const variant of variants) {
            const hasAllSelectedOptions = selectedOptionsArray.every((selectedValue) =>
                variant.selectedOptions?.some((option) => option.value === selectedValue),
            );
            if (hasAllSelectedOptions) {
                return {
                    ...variant,
                    copyPrice: variant.price,
                };
            }
        }
        return null;
    };

    return (
        <InspectorEditableBlock bundleType={bundleType} settingKey="variantSelector">
            <div className={styles.optionsOuterContainer}>
                <div
                    style={{
                        maxWidth: "100%",
                        position: "relative",
                    }}
                >
                    <select
                        className={`${styles.select} ${styles.selectOverride}`}
                        name={"product_variants"}
                        style={{
                            padding: "4px !important",
                            // @ts-expect-error required
                            "--customColor": bundlesDesign?.variant_selector_text_color ?? "#383838",
                            fontWeight: bundlesDesign?.variant_selector_text_style?.split(" ")[0] ?? "450",
                            fontStyle: bundlesDesign?.variant_selector_text_style?.split(" ")[1] ?? "normal",
                            fontFamily:
                                bundlesDesign?.bundle_widget_font_family == "Custom Store Font"
                                    ? "inherit"
                                    : bundlesDesign?.bundle_widget_font_family,
                        }}
                        id={selected[productCardIndex]?.title} // <-- Unique ID
                        onChange={(e) => handleOptionChange(e)}
                        value={selected[productCardIndex]?.title ? selected[productCardIndex]?.title : ""}
                    >
                        {variants.map((value: any, idx: number) => (
                            <option
                                className={styles.option}
                                key={idx}
                                value={value.title}
                                disabled={value?.quantityAvailable <= 0 && !value?.availableForSale}
                            >
                                {value?.quantityAvailable <= 0 && !value?.availableForSale
                                    ? `${value.title} - N/A`
                                    : value.title}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </InspectorEditableBlock>
    );
};

const FreeGiftItem = ({
    index,
    _pd,
    standAlone,
    bundleConfig,
    updateProductVariants,
    money_format,
    currency,
}: {
    _pd: ProductObj;
    index: number;
    standAlone: boolean;
    targetForm?: any;
    bundleConfig: StandardConfigInterface | BxgyConfigInterface;
    setFreeGiftSelectedVariants?: any;
    updateProductVariants?: any;
    currency: any;
    money_format: string;
}) => {
    const [selectedVariants, setSelectedVariants] = useState<Array<VariantsObj>>(
        new Array(_pd.quantity).fill(_pd.variants.find((vv: any) => vv.availableForSale == true) || _pd.variants[0]),
    );

    useEffect(() => {
        const updatedSelectedVariants = selectedVariants?.map((variant: any) => ({
            ...variant,
            free_gift: true,
        }));
        _pd.selectedVariants = updatedSelectedVariants;
        updateProductVariants(index, updatedSelectedVariants);
    }, [selectedVariants]);

    useEffect(() => {
        const svs = new Array(_pd.quantity).fill(
            _pd.variants.find((vv: any) => vv.availableForSale == true) || _pd.variants[0],
        );
        setSelectedVariants(svs);
    }, [_pd]);

    const isProductAvailable = useMemo(() => {
        return _pd.variants.some(
            (vv: any) => (vv.quantityAvailable && vv.quantityAvailable > 0) || vv.availableForSale,
        );
    }, [_pd]);

    const userShopname = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const effectiveMetafield = buildMetafieldFromSwatchOptions({
        product: _pd,
        bundleConfig: bundleConfig,
    });

    const getNewPriceLabel = () => {
        const priceLabel = getPriceLabel(
            selectedVariants?.reduce((total, sv) => total + +sv?.price, 0),
            money_format,
            currency,
        );

        return (
            <div
                style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}
                className={styles.fgPriceWrapper}
            >
                <span className={styles.fgText}>{bundleConfig?.free_text}</span>
                <span
                    style={{
                        whiteSpace: "nowrap",
                        color: bundleConfig?.bundles_design?.product_price_color || "#030303",
                        fontSize: bundleConfig?.bundles_design.product_price_size,
                        fontWeight: bundleConfig?.bundles_design?.product_price_style?.split(" ")[0],
                        fontStyle: bundleConfig?.bundles_design?.product_price_style?.split(" ")[1],
                    }}
                    className={styles.fgPriceLabel}
                >
                    {priceLabel}
                </span>
            </div>
        );
    };

    return (
        <>
            {index > 0 && (
                <span
                    style={{
                        display: "flex",
                        height: "1px",
                        width: "98%",
                        background: hexToRgba(bundleConfig?.bundles_design?.free_gift_header_bg_color, 0.3),
                        margin: "auto",
                        marginTop: "6px",
                        marginBottom: "6px",
                    }}
                ></span>
            )}
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="freeGiftProduct">
                <div
                    className={styles.freeGiftContainer}
                    title={_pd.title}
                    style={{ marginTop: 5, alignItems: _pd.variants.length > 1 ? "flex-start" : "center" }}
                >
                    <div className={styles.freeGiftImgWrapper}>
                        <img
                            src={_pd?.images[0]?.originalSrc}
                            alt=""
                            style={{
                                width: "100%",
                                height: "100%",
                                padding: 2,
                                borderRadius: 8,
                            }}
                            className={styles.fgImg}
                            // onClick={() => {
                            //     if (standAlone && !window.location.pathname.includes(_pd.handle)) {
                            //         window.open(`https://${window?.location?.host}/products/${_pd.handle}`, "_blank");
                            //     } else if (!window.location.pathname.includes(_pd.handle)) {
                            //         window.open(`https://${shopify?.config?.shop}/products/${_pd.handle}`, "_blank");
                            //     }
                            // }}
                        />
                        {_pd?.selectedVariants?.length > 1 && (
                            <div className={styles.freeGiftBadge}>x{`${_pd?.selectedVariants?.length}`}</div>
                        )}
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "5px",
                            flexWrap: "wrap",
                            lineHeight: "normal",
                            width: "100%",
                        }}
                    >
                        {bundleConfig?.bundle_type == BundleTypes.VOLUME_DISCOUNT &&
                        // @ts-expect-error required
                        bundleConfig?.free_gift_template == "modern" ? (
                            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="freeGiftText">
                                <p
                                    className={styles.freeGiftText}
                                    style={{
                                        fontFamily:
                                            bundleConfig.bundles_design?.bundle_widget_font_family ==
                                            "Custom Store Font"
                                                ? "inherit"
                                                : bundleConfig.bundles_design?.bundle_widget_font_family,
                                        color: bundleConfig?.bundles_design?.free_gift_header_color,
                                        fontSize: bundleConfig?.bundles_design.free_gift_header_size,
                                        fontWeight: bundleConfig?.bundles_design?.free_gift_header_style?.split(" ")[0],
                                        fontStyle: bundleConfig?.bundles_design?.free_gift_header_style?.split(" ")[1],
                                        // padding: "8px 10px",
                                        borderBottom: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                                    }}
                                >
                                    {bundleConfig?.free_gift_text}
                                </p>
                            </InspectorEditableBlock>
                        ) : (
                            <p
                                className={styles.freeGiftText}
                                style={{
                                    fontFamily:
                                        bundleConfig.bundles_design?.bundle_widget_font_family == "Custom Store Font"
                                            ? "inherit"
                                            : bundleConfig.bundles_design?.bundle_widget_font_family,
                                    color: bundleConfig?.bundles_design?.free_gift_text_color,
                                    fontSize: bundleConfig?.bundles_design.free_gift_text_size,
                                    fontWeight: bundleConfig?.bundles_design?.free_gift_text_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design?.free_gift_text_style?.split(" ")[1],
                                    lineHeight: "normal",
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: "10px",
                                }}
                            >
                                {_pd.title}
                                {getNewPriceLabel()}
                            </p>
                        )}

                        {!isProductAvailable && <span style={{ fontSize: "12px", color: "red" }}>Out of stock!</span>}

                        {_pd.variants.length > 1 && isProductAvailable && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                }}
                            >
                                {selectedVariants.map((_: any, i: number) =>
                                    userShopname === "typsy-beauty.myshopify.com" ? (
                                        <Swatches
                                            bundlesDesign={bundleConfig?.bundles_design}
                                            key={i}
                                            variants={_pd.variants}
                                            handleChange={setSelectedVariants}
                                            selected={selectedVariants}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                            options={_pd.options || []}
                                        />
                                    ) : bundleConfig?.show_swatches && effectiveMetafield ? (
                                        <CustomSwatches
                                            key={i}
                                            variants={_pd.variants}
                                            handleChange={setSelectedVariants}
                                            selected={selectedVariants}
                                            options={_pd.options || []}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                            metafield={effectiveMetafield}
                                            bundlesDesign={bundleConfig?.bundles_design}
                                            bundleConfig={bundleConfig}
                                        />
                                    ) : (
                                        <VariantSelectorV2
                                            key={i}
                                            variants={_pd.variants}
                                            handleChange={setSelectedVariants}
                                            selected={selectedVariants}
                                            productCardIndex={i}
                                            standAlone={standAlone}
                                            bundlesDesign={bundleConfig?.bundles_design}
                                            options={_pd.options || []}
                                            metafield={effectiveMetafield}
                                            bundleConfig={bundleConfig}
                                        />
                                    ),
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </InspectorEditableBlock>
        </>
    );
};

export const FreeGiftComponent = ({
    products,
    bundleConfig,
    standAlone,
    targetForm,
    setFreeGiftSelectedVariants = null,
    active,
    index,
    money_format,
    currency,
}: {
    products: any;
    bundleConfig: StandardConfigInterface | BxgyConfigInterface;
    standAlone: any;
    targetForm: any;
    setFreeGiftSelectedVariants?: any | null;
    active?: number;
    index?: number;
    money_format: string;
    currency: any;
}) => {
    const [_products, setProducts] = useState<any>([]);

    useEffect(() => {
        setProducts(products);
        if (bundleConfig?.bundle_type == BundleTypes.STANDARD) bundleConfig.free_gifts = products;
    }, [products]);
    useEffect(() => {
        if (setFreeGiftSelectedVariants && active == index) {
            setFreeGiftSelectedVariants(() => {
                const temp: any[] = [];
                products.forEach((pd: ProductObj) => {
                    temp.push(...pd.selectedVariants);
                });
                return temp;
            });
        }
    }, [_products, index, active]);

    const updateProductVariants = (index: number, selectedVariants: VariantsObj[]) => {
        if (selectedVariants.length) {
            setProducts((ps: ProductObj[]) => {
                const temp: any[] = [...ps];
                temp[index].selectedVariants = selectedVariants;
                return temp;
            });
        }
    };

    return (
        <div
            style={{
                border: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                // @ts-expect-error required
                ...([BundleTypes.VOLUME_DISCOUNT]?.includes(bundleConfig?.bundle_type) && {
                    border: "none",
                }),
                borderRadius:
                    bundleConfig?.bundle_type === "volume" &&
                    [
                        AvailableVolumeTemplates.VERTICAL_BASIC,
                        AvailableVolumeTemplates.VERTICAL_MODERN,
                        AvailableVolumeTemplates.VERTICAL_CLASSIC,
                        // @ts-expect-error required
                    ].includes(bundleConfig?.selected_template_volume)
                        ? undefined
                        : bundleConfig?.bundles_design?.border_radius,
                // borderBottomLeftRadius: bundleConfig?.bundles_design?.add_to_cart_border_radius,
                // borderBottomRightRadius: bundleConfig?.bundles_design?.add_to_cart_border_radius,
                width: "100%",
                backgroundColor: bundleConfig.bundles_design?.free_gift_bg_color,
                overflow: "hidden",
                paddingBottom: bundleConfig?.free_gift_text?.length ? "8px" : "4px",
            }}
            className={styles.freeGiftComponentContainer}
        >
            {!(
                bundleConfig?.bundle_type === BundleTypes.VOLUME_DISCOUNT &&
                // @ts-expect-error required
                (!bundleConfig?.free_gift_template || bundleConfig?.free_gift_template === "modern")
            ) &&
                !!bundleConfig?.free_gift_text?.length && (
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="freeGiftText">
                        <p
                            className={styles.freeGiftText}
                            style={{
                                backgroundColor: bundleConfig.bundles_design?.free_gift_header_bg_color,
                                fontFamily:
                                    bundleConfig.bundles_design?.bundle_widget_font_family == "Custom Store Font"
                                        ? "inherit"
                                        : bundleConfig.bundles_design?.bundle_widget_font_family,
                                color: bundleConfig?.bundles_design?.free_gift_header_color,
                                fontSize: bundleConfig?.bundles_design.free_gift_header_size,
                                fontWeight: bundleConfig?.bundles_design?.free_gift_header_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design?.free_gift_header_style?.split(" ")[1],
                                padding: "8px 10px",
                                borderBottom: `${bundleConfig?.bundles_design?.border_width}px solid ${bundleConfig?.bundles_design?.product_border_color}`,
                                // @ts-expect-error required
                                ...([BundleTypes.VOLUME_DISCOUNT]?.includes(bundleConfig?.bundle_type) && {
                                    border: "none",
                                }),
                            }}
                        >
                            {bundleConfig?.free_gift_text}
                        </p>
                    </InspectorEditableBlock>
                )}
            <div
                style={{
                    padding: "1px 8px",
                }}
            >
                {_products?.map((_pd: any, index: number) => (
                    <FreeGiftItem
                        index={index}
                        _pd={_pd}
                        standAlone={standAlone}
                        bundleConfig={bundleConfig}
                        targetForm={targetForm}
                        updateProductVariants={updateProductVariants}
                        key={_pd.id}
                        currency={currency}
                        money_format={money_format}
                    />
                ))}
            </div>
        </div>
    );
};

function isStandardConfig(config: any): config is StandardConfigInterface {
    return config?.bundle_type === BundleTypes.STANDARD;
}
function isBxgyConfig(config: any): config is BxgyConfigInterface {
    return config?.bundle_type === BundleTypes.BXGY;
}

const BundlePreviewFooter: React.FC<BundlePreviewFooter> = ({
    isFBT,
    standAlone,
    totalPrice,
    finalPrice,
    discountedPrice,
    compareAtPriceTotal,
    money_format,
    currency,
    bundleConfig,
    loading,
    otherLoading = false,
    unavailableDiscountSelected,
    addToCart,
    targetForm,
    isMainProductOutOfStock,
    isMobile,
    bundleWrapperWidth = -1,
    quantity = 1,
    setQuantity = () => {},
}) => {
    const isUnavailable = Object.values(unavailableDiscountSelected).some((arr: any) => arr?.length > 0);
    const [selectedProducts, setSelectedProducts] = useState(0);
    const [isCtaDisabled, setIsCtaDisabled] = useState(false);
    // used in product_addon_popup bundle, for skipping the offers and the main_product only
    const [addMainProductLoading, setAddMainProductLoading] = useState(false);
    const userShopname: any = standAlone ? window.kefi_bundles.shop : shopify.config.shop;
    const addMainProduct = async () => {
        setAddMainProductLoading(true);
        await addBundleToCart(
            bundleConfig?.products?.slice(0, 1),
            {},
            bundleConfig.form_id || "",
            !!bundleConfig?.general_settings?.redirect_to_checkout,
            bundleConfig?.bundle_type,
        );
        setAddMainProductLoading(false);

        window.isProductAddOnPopupModalOpen = false;
        const event = new Event("productAddOnPopupStateChange");
        window.dispatchEvent(event);
    };

    const renderTotal = () => {
        if (
            bundleConfig?.bundle_type === BundleTypes.BB_PLACEHOLDER ||
            bundleConfig?.bundle_type === BundleTypes.PRODUCT_ADDONS ||
            bundleConfig?.bundle_type === BundleTypes.VOLUME_DISCOUNT
        ) {
            return null;
        }

        return (
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="footerTotalBar">
                <div
                    className={styles.totalBar}
                    style={{
                        fontWeight: bundleConfig?.bundles_design.total_footer_text_style?.split(" ")[0],
                        fontStyle: bundleConfig?.bundles_design.total_footer_text_style?.split(" ")[1],
                        borderRadius: bundleConfig?.bundles_design?.border_radius,
                        background:
                            bundleConfig.bundle_type == BundleTypes.PRODUCT_ADDONS ||
                            bundleConfig?.bundle_type == BundleTypes.VOLUME_DISCOUNT
                                ? "transparent"
                                : bundleConfig?.bundles_design?.total_footer_background_color,
                        alignItems: "baseline",
                    }}
                >
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="footerTotalText">
                        <div
                            className={styles.totalBar_title}
                            style={{
                                fontSize: bundleConfig?.bundles_design.total_footer_text_size,
                                color: bundleConfig?.bundles_design.total_footer_text_color,
                                display: "flex",
                                width: "max-content",
                            }}
                        >
                            {bundleConfig?.footer_total_text || "Total"}
                            <span className={styles.totalBarDiscountOffValue}>
                                {getDiscountValueWithSign(
                                    bundleConfig?.discount_type,
                                    bundleConfig?.discount_value || 0,
                                    currency,
                                    money_format,
                                    bundleConfig?.footer_text,
                                ) && ":"}
                                &nbsp;
                                {bundleConfig?.bundle_type !== "bxgy" &&
                                    bundleConfig?.bundle_type !== BundleTypes.BUY_N && (
                                        <span style={{ ...(isFBT && { color: "#4CAF50" }) }}>
                                            {getFinalPrice("none", 0, finalPrice) <= 0 ? (
                                                <>&nbsp;{bundleConfig?.free_text || "Free"}</>
                                            ) : (
                                                <>
                                                    {bundleConfig?.discount_type !== "fixed" &&
                                                        getDiscountValueWithSign(
                                                            bundleConfig?.discount_type,
                                                            bundleConfig?.discount_value || 0,
                                                            currency,
                                                            money_format,
                                                            bundleConfig?.footer_text,
                                                        )}
                                                </>
                                            )}
                                        </span>
                                    )}
                            </span>
                        </div>
                    </InspectorEditableBlock>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            ...((bundleConfig?.bundle_type === BundleTypes.MIX_MATCH ||
                                bundleConfig?.bundle_type == BundleTypes.BUY_N ||
                                bundleConfig?.bundle_type == BundleTypes.MIX_MATCH_SECTIONAL ||
                                bundleConfig?.bundle_type === BundleTypes.STANDARD ||
                                bundleConfig?.bundle_type === BundleTypes.BXGY) && {
                                flexDirection: "row",
                                alignItems: "center",
                                gap: "5px",
                            }),
                        }}
                        className={styles.footerPriceWrapper}
                    >
                        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="footerTotalPrice">
                            <div
                                style={{
                                    fontSize: bundleConfig?.bundles_design.total_footer_price_text_size,
                                    fontWeight:
                                        bundleConfig?.bundles_design.total_footer_price_text_style?.split(" ")[0],
                                    fontStyle:
                                        bundleConfig?.bundles_design.total_footer_price_text_style?.split(" ")[1],
                                    color: bundleConfig?.bundles_design.total_footer_text_color,
                                    ...(isFBT && { display: "flex", width: "max-content" }),
                                }}
                                className={styles.footerFinalPrice}
                            >
                                {finalPrice <= 0
                                    ? bundleConfig?.free_text || "Free"
                                    : getPriceLabel(finalPrice, money_format, currency)}
                            </div>
                        </InspectorEditableBlock>
                        {bundleConfig?.discount_type !== "none" &&
                            (finalPrice !== totalPrice + (discountedPrice || 0) ||
                                (bundleConfig.use_compare_at_price && finalPrice !== compareAtPriceTotal)) && (
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="footerCompareAtPrice"
                                >
                                    <div
                                        style={{
                                            fontSize: bundleConfig?.bundles_design?.product_price_size,
                                            fontWeight: bundleConfig?.bundles_design.product_price_style?.split(" ")[0],
                                            fontStyle: bundleConfig?.bundles_design.product_price_style?.split(" ")[1],
                                            color: bundleConfig?.bundles_design?.product_price_color,
                                            lineHeight: "normal",
                                        }}
                                        className={styles.footerStrikeOffPrice}
                                    >
                                        {!bundleConfig?.use_compare_at_price
                                            ? getPriceLabel(totalPrice + (discountedPrice || 0), money_format, currency)
                                            : getPriceLabel(compareAtPriceTotal || 0, money_format, currency)}
                                    </div>
                                </InspectorEditableBlock>
                            )}
                    </div>
                </div>
            </InspectorEditableBlock>
        );
    };

    const renderTotalv2 = () => {
        const animealExtraAddValue = [
            "animeal-store.myshopify.com",
            MAYUR_TEST_STORE,
            "quickstart-f02489ba.myshopify.com",
        ].includes(userShopname)
            ? totalPrice * 0.1
            : 0;

        return (
            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="footerTotalBar">
                <div
                    className={styles.totalBarV2Container}
                    style={{
                        fontWeight: bundleConfig?.bundles_design.total_footer_text_style?.split(" ")[0],
                        fontStyle: bundleConfig?.bundles_design.total_footer_text_style?.split(" ")[1],
                        background:
                            bundleConfig.bundle_type == BundleTypes.PRODUCT_ADDONS ||
                            bundleConfig?.bundle_type == BundleTypes.VOLUME_DISCOUNT
                                ? "transparent"
                                : bundleConfig?.bundles_design?.total_footer_background_color,
                    }}
                >
                    <div className={styles.totalBarRow}>
                        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="footerTotalText">
                            <div
                                className={styles.totalBar_title}
                                style={{
                                    fontSize: bundleConfig?.bundles_design.total_footer_text_size,
                                    color: bundleConfig?.bundles_design.total_footer_text_color,
                                    display: "flex",
                                    width: "max-content",
                                }}
                            >
                                {bundleConfig?.footer_total_text || "Total"}
                                {bundleConfig?.bundle_type !== "bxgy" && (
                                    <>
                                        :{" "}
                                        {getFinalPrice("none", 0, finalPrice) <= 0
                                            ? bundleConfig?.free_text || "FREE"
                                            : getDiscountValueWithSign(
                                                  bundleConfig?.discount_type,
                                                  bundleConfig?.discount_value || 0,
                                                  currency,
                                                  money_format,
                                              )}
                                    </>
                                )}
                            </div>
                        </InspectorEditableBlock>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                            }}
                            className={styles.v2PriceWrapper}
                        >
                            <InspectorEditableBlock
                                bundleType={bundleConfig?.bundle_type}
                                settingKey="footerTotalPrice"
                            >
                                <div
                                    style={{
                                        fontSize: bundleConfig?.bundles_design.total_footer_price_text_size,
                                        fontWeight:
                                            bundleConfig?.bundles_design.total_footer_price_text_style?.split(" ")[0],
                                        fontStyle:
                                            bundleConfig?.bundles_design.total_footer_price_text_style?.split(" ")[1],
                                        color: bundleConfig?.bundles_design.total_footer_text_color,
                                        ...(isFBT && { display: "flex", width: "max-content" }),
                                        whiteSpace: "nowrap",
                                    }}
                                    className={styles.v2FinalPrice}
                                >
                                    {finalPrice <= 0
                                        ? bundleConfig?.free_text || "Free"
                                        : getPriceLabel(finalPrice - animealExtraAddValue, money_format, currency)}
                                </div>
                            </InspectorEditableBlock>
                            {bundleConfig?.discount_type !== "none" &&
                                (finalPrice !== totalPrice + (discountedPrice || 0) ||
                                    (bundleConfig.use_compare_at_price && finalPrice !== compareAtPriceTotal)) && (
                                    <InspectorEditableBlock
                                        bundleType={bundleConfig?.bundle_type}
                                        settingKey="footerCompareAtPrice"
                                    >
                                        <div
                                            style={{
                                                fontSize: bundleConfig?.bundles_design?.product_price_size,
                                                fontWeight:
                                                    bundleConfig?.bundles_design.product_price_style?.split(" ")[0],
                                                fontStyle:
                                                    bundleConfig?.bundles_design.product_price_style?.split(" ")[1],
                                                color: bundleConfig?.bundles_design?.product_price_color,
                                                whiteSpace: "nowrap",
                                            }}
                                            className={styles.v2StrikePrice}
                                        >
                                            {!bundleConfig?.use_compare_at_price
                                                ? getPriceLabel(
                                                      totalPrice + (discountedPrice || 0),
                                                      money_format,
                                                      currency,
                                                  )
                                                : getPriceLabel(compareAtPriceTotal || 0, money_format, currency)}
                                        </div>
                                    </InspectorEditableBlock>
                                )}
                        </div>
                    </div>

                    <div
                        className={styles.saveWrapper}
                        style={{
                            color: bundleConfig?.bundles_design.total_footer_text_color,
                            fontSize: bundleConfig?.bundles_design.footer_save_money_text_size,
                        }}
                    >
                        <span>Save:</span>
                        <span
                            className={styles.saveValue}
                            style={{
                                color: bundleConfig?.bundles_design?.footer_save_money_text_color,
                                fontWeight: bundleConfig?.bundles_design.footer_save_money_text_style?.split(" ")[0],
                                fontStyle: bundleConfig?.bundles_design.footer_save_money_text_style?.split(" ")[1],
                            }}
                        >
                            {getPriceLabel(
                                totalPrice + (discountedPrice || 0) - finalPrice + animealExtraAddValue,
                                money_format,
                                currency,
                            )}
                        </span>
                    </div>
                </div>
            </InspectorEditableBlock>
        );
    };

    const PlaceholderOrderTotal = () => {
        return (
            <>
                {bundleConfig?.discount_type != "fixed" &&
                    bundleConfig?.discount_type != "none" &&
                    bundleConfig?.discount_value > 0 &&
                    totalPrice > 0 && (
                        <div className={styles.summaryBox}>
                            <div
                                className={styles.summaryRow}
                                style={{
                                    fontSize: bundleConfig?.bundles_design.order_summary_header_text_size,
                                    color: bundleConfig?.bundles_design.order_summary_header_text_color,
                                    fontWeight:
                                        bundleConfig?.bundles_design.order_summary_header_text_style?.split(" ")[0],
                                    fontStyle:
                                        bundleConfig?.bundles_design.order_summary_header_text_style?.split(" ")[1],
                                }}
                            >
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="orderSummaryLabel"
                                >
                                    <span className={styles.label}>{bundleConfig?.order_summary_subtotal}</span>
                                </InspectorEditableBlock>
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="orderSummaryValue"
                                >
                                    <span
                                        className={styles.val}
                                        style={{
                                            fontSize: bundleConfig?.bundles_design.order_summary_rows_text_size,
                                            color: bundleConfig?.bundles_design.order_summary_rows_text_color,
                                            fontWeight:
                                                bundleConfig?.bundles_design.order_summary_rows_text_style?.split(
                                                    " ",
                                                )[0],
                                            fontStyle:
                                                bundleConfig?.bundles_design.order_summary_rows_text_style?.split(
                                                    " ",
                                                )[1],
                                        }}
                                    >
                                        {getPriceLabel(totalPrice, money_format, currency)}
                                    </span>
                                </InspectorEditableBlock>
                            </div>

                            <div
                                className={styles.summaryRow}
                                style={{
                                    fontSize: bundleConfig?.bundles_design.order_summary_header_text_size,
                                    color: bundleConfig?.bundles_design.order_summary_header_text_color,
                                    fontWeight:
                                        bundleConfig?.bundles_design.order_summary_header_text_style?.split(" ")[0],
                                    fontStyle:
                                        bundleConfig?.bundles_design.order_summary_header_text_style?.split(" ")[1],
                                }}
                            >
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="orderSummaryLabel"
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: 4,
                                        }}
                                    >
                                        <span className={styles.label}>{bundleConfig?.order_summary_discount}</span>
                                        {bundleConfig?.discount_type == "percentage" && (
                                            <span>
                                                (
                                                {getFinalPrice("none", 0, finalPrice) <= 0
                                                    ? bundleConfig?.free_text || "Free"
                                                    : getDiscountValueWithSign(
                                                          bundleConfig?.discount_type,
                                                          bundleConfig?.discount_value || 0,
                                                          currency,
                                                          money_format,
                                                          bundleConfig?.footer_text,
                                                      )}
                                                )
                                            </span>
                                        )}
                                    </div>
                                </InspectorEditableBlock>
                                <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="specialText">
                                    <span
                                        className={styles.val}
                                        style={{
                                            // fontSize: bundleConfig?.bundles_design.order_summary_rows_text_size,
                                            // color: bundleConfig?.bundles_design.order_summary_rows_text_color,
                                            // fontWeight:
                                            //     bundleConfig?.bundles_design.order_summary_rows_text_style?.split(
                                            //         " ",
                                            //     )[0],
                                            // fontStyle:
                                            //     bundleConfig?.bundles_design.order_summary_rows_text_style?.split(
                                            //         " ",
                                            //     )[1],
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderRadius: 18,
                                            background: bundleConfig?.bundles_design?.special_text_background_color,
                                            color: bundleConfig?.bundles_design?.special_text_color,
                                            fontSize: bundleConfig?.bundles_design?.special_text_font_size,
                                            padding: "4px 10px",
                                            fontWeight: bundleConfig?.bundles_design?.special_text_style?.split(" ")[0],
                                            fontStyle: bundleConfig?.bundles_design?.special_text_style?.split(" ")[1],
                                        }}
                                    >
                                        - {getPriceLabel(totalPrice - finalPrice, money_format, currency)}
                                    </span>
                                </InspectorEditableBlock>
                            </div>
                        </div>
                    )}
                <div
                    className={styles.totalBar}
                    style={{
                        fontWeight: bundleConfig?.bundles_design.total_footer_text_style?.split(" ")[0],
                        fontStyle: bundleConfig?.bundles_design.total_footer_text_style?.split(" ")[1],
                        alignItems: "baseline",
                        paddingInline: 12,
                        display: totalPrice <= 0 ? "none" : "flex",
                    }}
                >
                    <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="totalFooterText">
                        <div
                            className={styles.totalBar_title}
                            style={{
                                fontSize: bundleConfig?.bundles_design.total_footer_text_size,
                                color: bundleConfig?.bundles_design.total_footer_text_color,
                                display: "flex",
                                width: "max-content",
                            }}
                        >
                            {bundleConfig?.footer_total_text || "Total"}
                        </div>
                    </InspectorEditableBlock>
                    <div className={styles.placeholderFooterPriceWrapper}>
                        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="footerTotalPrice">
                            <div
                                style={{
                                    fontSize: bundleConfig?.bundles_design.total_footer_price_text_size,
                                    fontWeight:
                                        bundleConfig?.bundles_design.total_footer_price_text_style?.split(" ")[0],
                                    fontStyle:
                                        bundleConfig?.bundles_design.total_footer_price_text_style?.split(" ")[1],
                                    color: bundleConfig?.bundles_design.total_footer_price_text_color,
                                    ...(isFBT && { display: "flex", width: "max-content" }),
                                }}
                                className={styles.placeholderFooterFinalPrice}
                            >
                                {finalPrice <= 0
                                    ? bundleConfig?.free_text || "Free"
                                    : getPriceLabel(finalPrice, money_format, currency)}
                            </div>
                        </InspectorEditableBlock>
                        {bundleConfig?.discount_type == "fixed" &&
                            (() => {
                                const strikePrice = !bundleConfig?.use_compare_at_price
                                    ? totalPrice + (discountedPrice || 0)
                                    : compareAtPriceTotal || 0;

                                return strikePrice > finalPrice;
                            })() && (
                                <InspectorEditableBlock
                                    bundleType={bundleConfig?.bundle_type}
                                    settingKey="footerCompareAtPrice"
                                >
                                    <div
                                        style={{
                                            fontSize: bundleConfig?.bundles_design?.product_price_size,
                                            fontWeight: bundleConfig?.bundles_design.product_price_style?.split(" ")[0],
                                            fontStyle: bundleConfig?.bundles_design.product_price_style?.split(" ")[1],
                                            color: bundleConfig?.bundles_design?.product_price_color,
                                        }}
                                        className={styles.placeholderFooterStrikePrice}
                                    >
                                        {!bundleConfig?.use_compare_at_price
                                            ? getPriceLabel(totalPrice + (discountedPrice || 0), money_format, currency)
                                            : getPriceLabel(compareAtPriceTotal || 0, money_format, currency)}
                                    </div>
                                </InspectorEditableBlock>
                            )}
                    </div>
                </div>
            </>
        );
    };

    useEffect(() => {
        if (bundleConfig?.bundle_type == BundleTypes.PRODUCT_ADDONS_POPUP) {
            if (bundleConfig?.products?.length) {
                try {
                    let arr: ProductObj[] = [];
                    if (standAlone) {
                        arr = [...bundleConfig.products];
                    } else {
                        if (bundleConfig?.product_add_on_offered_products_type == "recommendations") {
                            arr = bundleConfig?.products?.filter(
                                (_: any, idx: number) => idx < bundleConfig?.max_limit_of_recommendations,
                            );
                        } else {
                            arr = [...bundleConfig.products];
                        }
                    }

                    if (standAlone) {
                        setSelectedProducts(
                            arr.filter((product: ProductObj) => !product.required_product && product?.checked)
                                ?.length ?? 0,
                        );
                    } else {
                        setSelectedProducts(arr.filter((product: ProductObj) => product?.checked)?.length ?? 0);
                    }
                } catch (err) {
                    setSelectedProducts(0);
                    console._log(err);
                }
            } else {
                setSelectedProducts(0);
            }
        }
    }, [bundleConfig?.products, bundleConfig?.max_limit_of_recommendations]);

    useEffect(() => {
        const handleDisableCtaChanged = (e: Event) => {
            const customEvent = e as CustomEvent<{ disabled: boolean; availableQuantity: number }>;
            setIsCtaDisabled(customEvent.detail.disabled);
            renderBundleErrorMessage(
                customEvent.detail.disabled && customEvent.detail.availableQuantity > 1
                    ? `Oops! Just ${customEvent.detail.availableQuantity} item${
                          customEvent.detail.availableQuantity > 1 ? "s" : ""
                      } available right now.`
                    : "",
                bundleConfig?.form_id,
            );
        };

        window.addEventListener("disableCtaChanged", handleDisableCtaChanged);

        return () => {
            window.removeEventListener("disableCtaChanged", handleDisableCtaChanged);
        };
    }, []);

    const renderCTAContent = () => {
        const ctaText = bundleConfig?.add_to_cart_cta_text || "";
        const hasShortcode = /{{price}}/.test(ctaText);

        const plainText = ctaText.replace(/{{price}}/g, "").trim();

        if (bundleConfig?.bundle_type === BundleTypes.VOLUME_DISCOUNT) {
            let hasProducts = false;

            if (bundleConfig?.bundles_design?.default_cta) {
                return <>{plainText}</>;
            }
            if (bundleConfig?.volume_product_selection === "all" && !standAlone) {
                hasProducts = !!window?.qb_product;
            } else {
                hasProducts = Array.isArray(bundleConfig?.products) && bundleConfig.products.length > 0;
            }

            if (!hasProducts || totalPrice <= 0) {
                return <>{plainText}</>;
            }
        }

        if (!hasShortcode) return ctaText;

        return (
            <div
                style={{
                    width: "100%",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "5px",
                    textAlign: "left",
                }}
            >
                <div style={{ flex: 1 }}>{plainText}</div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        whiteSpace: "nowrap",
                        wordBreak: "break-word",
                        flexShrink: 0,
                    }}
                >
                    <div>
                        {finalPrice <= 0
                            ? bundleConfig?.free_text || "Free"
                            : getPriceLabel(finalPrice, money_format, currency)}
                    </div>
                    {bundleConfig?.discount_type !== "none" && finalPrice !== compareAtPriceTotal && (
                        <div
                            style={{
                                textDecoration: "line-through",
                                fontSize: `calc(100% - 2px)`,
                                fontWeight: Math.max(
                                    Math.round(
                                        parseInt(
                                            bundleConfig?.bundles_design?.add_to_cart_text_style?.split(" ")[0] ||
                                                "400",
                                            10,
                                        ) / 2,
                                    ),
                                    100,
                                ),
                                lineHeight: "normal",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {getPriceLabel(compareAtPriceTotal || 0, money_format, currency)}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    let count = 0;
    const getFooterCount = () => {
        bundleConfig.products.forEach((item: any) => {
            if (item?.selectedVariants?.length > 0) {
                count += item.selectedVariants.length;
            }
        });

        return count;
    };

    const showQuantitySelector = [
        "dev-store-0124.myshopify.com",
        "verde-legion.myshopify.com",
        "led-equipped.myshopify.com",
        "extnutpro.myshopify.com",
        MAYUR_TEST_STORE,
        SPORTS_TOP_URL,
    ];

    return (
        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="footerContainer">
            <div
                className={`${styles.footer}`}
                style={{
                    borderTopLeftRadius: bundleConfig?.bundles_design?.border_radius,
                    borderTopRightRadius: bundleConfig?.bundles_design?.border_radius,
                    borderBottomLeftRadius: bundleConfig?.bundles_design?.border_radius,
                    borderBottomRightRadius: bundleConfig?.bundles_design?.border_radius,
                    background:
                        bundleConfig.bundle_type == BundleTypes.BB_PLACEHOLDER ||
                        (bundleConfig.bundle_type === BundleTypes.STANDARD &&
                            bundleConfig?.selected_template_standard !== AvailableStandardTemplates.HORIZONTAL_BASIC)
                            ? bundleConfig?.bundles_design?.order_summary_bg_color
                            : bundleConfig.bundle_type == BundleTypes.PRODUCT_ADDONS ||
                                bundleConfig?.bundle_type == BundleTypes.VOLUME_DISCOUNT
                              ? "transparent"
                              : bundleConfig?.bundles_design?.total_footer_background_color,
                    justifyContent: bundleConfig?.products?.length && isFBT ? "none" : "center",
                    marginTop:
                        userShopname === SPORTS_TOP_URL && bundleConfig?.bundle_type == BundleTypes.PRODUCT_ADDONS
                            ? 0
                            : isFBT ||
                                (bundleConfig?.bundleType === BundleTypes.STANDARD &&
                                    bundleConfig?.selected_template_standard !==
                                        AvailableStandardTemplates.HORIZONTAL_BASIC)
                              ? 2
                              : 15,
                    height: bundleConfig?.products?.length && isFBT ? "min-content" : "auto",
                    ...(bundleConfig.bundle_type === BundleTypes.PRODUCT_ADDONS_POPUP && {
                        position: "sticky",
                        bottom: "-8px",
                        zIndex: "110",
                        padding: "10px 0",
                        background: bundleConfig?.bundles_design?.total_footer_background_color,
                        transform: "scale(1.01)",
                        borderBottomLeftRadius: bundleConfig?.bundles_design?.border_radius,
                        borderBottomRightRadius: bundleConfig?.bundles_design?.border_radius,
                    }),
                    ...(bundleConfig.bundle_type === BundleTypes.FBT &&
                        isMobile && {
                            width: "100%",
                        }),

                    ...(isFBT &&
                        standAlone &&
                        bundleConfig?.selected_template_fbt === AvailableFBTTemplates.VERTICAL && {
                            width: "100%",
                        }),

                    ...(isDesktopUIForMixMatch(bundleWrapperWidth, bundleConfig.bundle_type) && {
                        width: "90%",
                        marginInline: "auto",
                        justifyContent: "flex-end",
                        flexDirection: "row",
                    }),
                }}
            >
                {/* Free Gift  */}
                {isStandardConfig(bundleConfig) &&
                    bundleConfig?.allow_free_gifts &&
                    bundleConfig?.free_gifts?.length > 0 && (
                        <FreeGiftComponent
                            products={bundleConfig?.free_gifts as ProductObj[]}
                            bundleConfig={bundleConfig}
                            standAlone={standAlone}
                            targetForm={targetForm}
                            money_format={money_format}
                            currency={currency}
                        />
                    )}
                {isBxgyConfig(bundleConfig) &&
                    bundleConfig?.allow_free_gifts &&
                    bundleConfig?.free_gifts?.length > 0 && (
                        <FreeGiftComponent
                            products={bundleConfig?.free_gifts as ProductObj[]}
                            bundleConfig={bundleConfig}
                            standAlone={standAlone}
                            targetForm={targetForm}
                            money_format={money_format}
                            currency={currency}
                        />
                    )}
                {/* <FreeGiftComponent
                                key={bc.id}
                                product={bc}
                                index={index}
                                bundleConfig={bundleConfig}
                                standAlone={standAlone}
                                targetForm={targetForm}
                            /> */}
                {[BundleTypes.PRODUCT_ADDONS, BundleTypes.PRODUCT_ADDONS_POPUP]?.includes(bundleConfig?.bundle_type) ? (
                    <></>
                ) : bundleConfig.bundle_type == "bxgy" &&
                  bundleConfig.selected_template_bxgy == AvailableBXGYTemplates.HORIZONTAL_BASIC ? (
                    totalPrice != null && (bundleConfig?.products?.length ? renderTotalv2() : <></>)
                ) : (
                    totalPrice != null && (bundleConfig?.products?.length ? renderTotal() : <></>)
                )}

                {bundleConfig.bundle_type === BundleTypes.BB_PLACEHOLDER ? PlaceholderOrderTotal() : null}

                <div
                    style={{
                        width: isDesktopUIForMixMatch(bundleWrapperWidth, bundleConfig.bundle_type)
                            ? "max-content"
                            : "100%",
                        display: "flex",
                        flexDirection: isMobile ? "column-reverse" : "row",
                        gap: isMobile ? 0 : "10px",
                        justifyContent: "stretch",
                        alignItems: "center",
                        paddingInline: 2,
                        // overflow: "hidden",
                        paddingTop:
                            bundleConfig.bundle_type === BundleTypes.STANDARD &&
                            bundleConfig?.selected_template_standard !== AvailableStandardTemplates.HORIZONTAL_BASIC
                                ? "8px"
                                : "0",
                        ...(userShopname === SPORTS_TOP_URL && {
                            flexDirection: "row",
                            gap: "10px",
                            alignItems: "stretch",
                        }),
                    }}
                    className={styles.addToCartWrapper}
                >
                    {(standAlone ? true : !bundleConfig?.bundles_design?.default_cta) &&
                        bundleConfig.bundle_type === BundleTypes.PRODUCT_ADDONS_POPUP && (
                            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="skipOfferCta">
                                <div style={{ width: "inherit" }}>
                                    <Button
                                        title={bundleConfig?.skip_offer_cta_text}
                                        onClick={standAlone ? addMainProduct : () => {}}
                                        loading={addMainProductLoading || otherLoading}
                                        inheritLoadingColor={true}
                                        style={{
                                            flex: 1,
                                            background: "transparent",
                                            textDecoration: "underline",
                                            borderRadius: bundleConfig?.bundles_design?.skip_offer_border_radius,
                                            color: bundleConfig?.bundles_design?.skip_offer_text_color,
                                            fontSize: bundleConfig?.bundles_design?.skip_offer_text_size,
                                            fontStyle:
                                                bundleConfig?.bundles_design?.skip_offer_text_style?.split(" ")[1],
                                            fontWeight:
                                                bundleConfig?.bundles_design?.skip_offer_text_style?.split(" ")[0],
                                            padding: "10px",
                                            border: "none",
                                            cursor: "pointer",
                                            flexGrow: 1,
                                            width: "100%",
                                            height: "auto",
                                            wordBreak: "keep-all",
                                            minWidth: "20%",
                                        }}
                                    />
                                </div>
                            </InspectorEditableBlock>
                        )}

                    {((bundleConfig?.bundle_type === BundleTypes.STANDARD &&
                        "bundle_as_product" in bundleConfig &&
                        bundleConfig.isProductPageVisible) ||
                        bundleConfig?.bundle_type === BundleTypes.PRODUCT_ADDONS) &&
                        showQuantitySelector.includes(userShopname) && (
                            <div
                                className={styles.quantityWrapper}
                                style={{
                                    width: isMobile ? "38.78%" : "23.38%",
                                    flex: isMobile ? "0 0 auto" : undefined,
                                    opacity: bundleConfig?.disableAddToCartButton ? 0.5 : 1,
                                    pointerEvents: bundleConfig?.disableAddToCartButton ? "none" : "auto",
                                }}
                            >
                                {["dev-store-0124.myshopify.com", "verde-legion.myshopify.com"].includes(userShopname)
                                    ? "Amount:"
                                    : ""}
                                <div className={styles.inputGroup}>
                                    <button
                                        type="button"
                                        className={styles.decrement}
                                        style={{
                                            fontSize:
                                                bundleConfig?.bundles_design?.add_to_cart_text_size < 50
                                                    ? bundleConfig?.bundles_design?.add_to_cart_text_size + 10
                                                    : 50,
                                            padding: isMobile ? "10px 6px" : "8px 10px",
                                        }}
                                        onClick={() => {
                                            setQuantity((prev) => Math.max(prev - 1, 1));
                                        }}
                                    >
                                        −
                                    </button>
                                    <input
                                        type="number"
                                        className={styles.quantityInput}
                                        value={quantity}
                                        style={{
                                            fontSize:
                                                bundleConfig?.bundles_design?.add_to_cart_text_size < 50
                                                    ? bundleConfig?.bundles_design?.add_to_cart_text_size + 5
                                                    : 50,
                                        }}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === "") {
                                                setQuantity(1);
                                                return;
                                            }

                                            const num = Number(val);
                                            if (isNaN(num) || num <= 0) return;

                                            setQuantity(num);
                                        }}
                                        onBlur={() => {
                                            let val = parseInt(String(quantity), 10);
                                            if (isNaN(val) || val <= 0) val = 1;

                                            setQuantity(val);
                                        }}
                                    />

                                    <button
                                        style={{
                                            fontSize:
                                                bundleConfig?.bundles_design?.add_to_cart_text_size < 50
                                                    ? bundleConfig?.bundles_design?.add_to_cart_text_size + 10
                                                    : 50,
                                            padding: isMobile ? "10px 6px" : "8px 10px",
                                        }}
                                        type="button"
                                        className={styles.increment}
                                        onClick={() => {
                                            setQuantity((prev) => prev + 1);
                                        }}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        )}

                    {(standAlone ? true : !bundleConfig?.bundles_design?.default_cta) && (
                        <Tooltip
                            text={
                                bundleConfig?.disableAddToCartButton &&
                                bundleConfig?.bundle_type !== BundleTypes.MIX_MATCH_SECTIONAL
                                    ? bundleConfig?.tooltipMessage
                                    : ""
                            }
                            position="top"
                            tooltipWrapperStyle={{ width: "100%" }}
                        >
                            <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="addToCartCta">
                                <div style={{ width: "inherit" }}>
                                    <Button
                                        title={
                                            bundleConfig?.disableAddToCartButton && userShopname === SPORTS_TOP_URL
                                                ? "SELECT AN OPTION"
                                                : isMainProductOutOfStock
                                                  ? bundleConfig?.main_product_out_of_stock_text ||
                                                    "Main product is out of stock"
                                                  : isUnavailable
                                                    ? "Some items are unavailable"
                                                    : bundleConfig?.bundle_type === BundleTypes.PRODUCT_ADDONS_POPUP &&
                                                        selectedProducts > 0
                                                      ? `${bundleConfig?.add_to_cart_cta_text} (${selectedProducts})`
                                                      : bundleConfig.bundle_type === BundleTypes.VOLUME_DISCOUNT
                                                        ? renderCTAContent()
                                                        : bundleConfig?.bundle_type ===
                                                                BundleTypes.MIX_MATCH_SECTIONAL &&
                                                            bundleConfig?.allow_bundle_condition &&
                                                            bundleConfig?.bundleConditionMessage
                                                          ? bundleConfig?.bundleConditionMessage
                                                          : bundleConfig?.bundle_type === BundleTypes.MIX_MATCH ||
                                                              bundleConfig?.bundle_type ===
                                                                  BundleTypes.MIX_MATCH_SECTIONAL
                                                            ? `${bundleConfig?.add_to_cart_cta_text} (${getFooterCount()})`
                                                            : bundleConfig?.add_to_cart_cta_text
                                        }
                                        onClick={
                                            standAlone && !otherLoading && !isMainProductOutOfStock
                                                ? addToCart
                                                : () => {}
                                        }
                                        loading={loading}
                                        className={
                                            bundleConfig?.bundles_design?.default_cta &&
                                            window.kefi_bundles.addToCartClasses
                                                ? `${window.kefi_bundles.addToCartClasses} ${styles.kefiAddToCart}`
                                                : ""
                                        }
                                        id="addToCartButton"
                                        disabledCta={
                                            (isCtaDisabled || bundleConfig?.disableAddToCartButton) && standAlone
                                        }
                                        inheritLoadingColor={true}
                                        style={{
                                            flex: 1,
                                            ...(bundleConfig?.bundles_design?.default_cta &&
                                            window.kefi_bundles.addToCartClasses
                                                ? {
                                                      fontFamily: "var(--font-body-family)",
                                                      position: "relative",
                                                      top: "unset",
                                                      left: "unset",
                                                      bottom: "unset",
                                                      right: "unset",
                                                      margin: "1px 0",
                                                      float: "unset",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                  }
                                                : {
                                                      background:
                                                          isUnavailable || isMainProductOutOfStock
                                                              ? "#A9A9A9"
                                                              : bundleConfig?.bundles_design?.add_to_cart_bg_color,
                                                      borderRadius:
                                                          bundleConfig?.bundles_design?.add_to_cart_border_radius,
                                                      color: bundleConfig?.bundles_design?.add_to_cart_text_color,
                                                      fontSize:
                                                          bundleConfig?.bundles_design?.add_to_cart_text_size < 50
                                                              ? bundleConfig?.bundles_design?.add_to_cart_text_size
                                                              : 50,
                                                      fontStyle:
                                                          bundleConfig?.bundles_design?.add_to_cart_text_style?.split(
                                                              " ",
                                                          )[1],
                                                      fontWeight:
                                                          bundleConfig?.bundles_design?.add_to_cart_text_style?.split(
                                                              " ",
                                                          )[0],
                                                      padding:
                                                          bundleConfig?.bundleType === BundleTypes.VOLUME_DISCOUNT
                                                              ? "16px 24px"
                                                              : "16px",
                                                      border: "none",
                                                      //   ...(bundleConfig.bundle_type !==
                                                      //       BundleTypes.MIX_MATCH_SECTIONAL && {
                                                      borderWidth:
                                                          bundleConfig?.bundles_design?.add_to_cart_border_width || 0,
                                                      borderStyle: "solid",
                                                      borderColor:
                                                          bundleConfig?.bundles_design?.add_to_cart_border_color ||
                                                          undefined,
                                                      //   }),
                                                  }),
                                            cursor:
                                                isUnavailable ||
                                                isMainProductOutOfStock ||
                                                (bundleConfig?.bundle_type === BundleTypes.MIX_MATCH_SECTIONAL &&
                                                    bundleConfig?.allow_bundle_condition &&
                                                    bundleConfig?.disableAddToCartButton)
                                                    ? "not-allowed"
                                                    : "pointer",
                                            minWidth: isFBT
                                                ? "280px"
                                                : bundleConfig.bundle_type === BundleTypes.PRODUCT_ADDONS_POPUP
                                                  ? "auto"
                                                  : "100%",
                                            width: "100%",
                                            height: "auto",

                                            ...(isDesktopUIForMixMatch(
                                                bundleWrapperWidth,
                                                bundleConfig.bundle_type,
                                            ) && {
                                                maxWidth: 300,
                                                minWidth: 300,
                                            }),
                                        }}
                                    />
                                </div>
                            </InspectorEditableBlock>
                        </Tooltip>
                    )}
                </div>
                {isFBT && <PoweredByKefiBranding bundleConfig={bundleConfig} />}
            </div>
        </InspectorEditableBlock>
    );
};

export default BundlePreviewFooter;
